import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployee } from '@/lib/pulse/useEmployee';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

/**
 * Auth landing route. Handles three arrival modes:
 *  1. OTP-code typed in SignIn.tsx → session already live, just route.
 *  2. Magic-link click, implicit flow → #access_token=&refresh_token=&type=…
 *     → setSession().
 *  3. Legacy PKCE link still in flight → ?code=<jwt>
 *     → exchangeCodeForSession() (only succeeds on originating browser).
 * After consumption, tokens are stripped from the URL via history.replaceState.
 */
export default function Callback() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const employee = useEmployee();
  const [exchanging, setExchanging] = useState(true);
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  // One-shot URL-credential consumption. Runs before any routing decisions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const hash = window.location.hash.startsWith('#')
          ? window.location.hash.slice(1)
          : '';
        const hp = new URLSearchParams(hash);
        const access_token = hp.get('access_token');
        const refresh_token = hp.get('refresh_token');

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('[auth/callback] exchangeCodeForSession failed', error);
            if (!cancelled) setExchangeError(error.message);
          }
        } else if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (error) {
            console.error('[auth/callback] setSession failed', error);
            if (!cancelled) setExchangeError(error.message);
          }
        }

        // Strip credentials from the address bar regardless of outcome.
        if (code || access_token || refresh_token) {
          window.history.replaceState({}, '', url.pathname);
        }
      } catch (e) {
        console.error('[auth/callback] unexpected error', e);
        if (!cancelled) setExchangeError((e as Error).message);
      } finally {
        if (!cancelled) setExchanging(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (exchanging) return;
    if (isLoading) return;
    if (!user) {
      navigate('/auth/signin', { replace: true });
      return;
    }
    if (employee.status === 'no_invite') {
      navigate('/auth/no-invite', { replace: true });
      return;
    }
    if (employee.status === 'ready') {
      // Route through /redirect so PostLoginRedirect can branch on
      // admin-vs-employee role and apply the seenWelcome first-time check.
      // Preserve ?next= for flows like /auth/claim?org=… that resume here.
      const next = new URLSearchParams(window.location.search).get('next');
      const dest = next ? `/redirect?next=${encodeURIComponent(next)}` : '/redirect';
      navigate(dest, { replace: true });
    }
  }, [exchanging, user, isLoading, employee, navigate]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Signing you in…</p>
        {exchangeError && (
          <p className="text-sm text-destructive">{exchangeError}</p>
        )}
        {employee.status === 'error' && (
          <p className="text-sm text-destructive">{employee.message}</p>
        )}
      </div>
    </main>
  );
}
